import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_AUTH_TOKEN } from '../../../core/http/http-context-tokens';
import { EstatisticaSicPublicaResponse, FiltroEstatisticaSic } from '../models/estatistica-sic-publica.model';

const RECURSO = `${environment.apiUrl}/sic/publico/estatisticas`;

/**
 * As estatísticas públicas do e-SIC do órgão (CDS-40), lidas pela página `/sic/estatisticas` — e
 * só por ela: o serviço é provido no componente, e sair da página descarta a leitura.
 *
 * ⚠️ O portal **não sabe nem envia o órgão**: o back-end o resolve pelo host. Sem token, e com o
 * erro local — falha e `429` viram o estado de erro da página, sem modal e sem zeros inventados.
 *
 * Reativo ao filtro: `filtrar` troca o sinal e o `httpResource` relê; a data vazia não vai à
 * requisição, e o back-end aplica o período padrão.
 */
@Service({ autoProvided: false })
export class EstatisticaSicPublicaService {
  private readonly filtro = signal<FiltroEstatisticaSic>({});

  private readonly estatisticasApi = httpResource<ResponseApi<EstatisticaSicPublicaResponse>>(() => {
    const { dataInicial, dataFinal } = this.filtro();

    const params: Record<string, string> = {};
    if (dataInicial) params['dataInicial'] = dataInicial;
    if (dataFinal) params['dataFinal'] = dataFinal;

    return {
      url: RECURSO,
      params,
      context: new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true).set(SKIP_AUTH_TOKEN, true),
    };
  });

  /** Enquanto a leitura não termina, a página não exibe número nenhum. */
  readonly carregando = computed(() => this.estatisticasApi.isLoading());

  /** A leitura falhou (inclusive por `429`): a página troca os blocos pela mensagem de erro. */
  readonly erro = computed(() => this.estatisticasApi.error() !== undefined);

  /** As estatísticas do período, ou `null` enquanto carrega ou quando a leitura falhou. */
  readonly estatisticas = computed<EstatisticaSicPublicaResponse | null>(() => {
    if (this.estatisticasApi.error() !== undefined) return null;

    return this.estatisticasApi.value()?.data ?? null;
  });

  /** Relê com o filtro informado; `{}` volta ao período padrão. */
  filtrar(filtro: FiltroEstatisticaSic): void {
    this.filtro.set({ ...filtro });
  }
}
